import type { ButtonProps } from '@mui/material/Button';
import { Notes_BehaviourType, partialParse } from '@tyro/api';
import { iconPickerMap } from '@tyro/core/icon-picker';
import { useCallback } from 'react';
import * as v from 'valibot';
import { useUpsertStudentBehaviour } from '../../../api/behaviour/upsert-behaviour';
import { useSaveTimelineHighlight } from '../../../api/student/timeline';
import type { NeedToKnowGroup } from '../../../store/student-actions';
import { eventTypeByBehaviourType } from '../../behaviour/create-behaviour-highlight-modal';

const iconPickerStringArray = Array.from(iconPickerMap.keys());

const NTKQuickActionBehaviourMeta = v.object({
  label: v.string(),
  icon: v.union(iconPickerStringArray.map((key) => v.literal(key))),
  __typename: v.literal('NTKQuickActionBehaviourMeta'),
  behaviourType: v.enum(Notes_BehaviourType),
  tagIds: v.array(v.number()),
  details: v.optional(v.string()),
  highlight: v.optional(
    v.object({
      title: v.string(),
      description: v.string(),
      assignedToPartyIds: v.array(v.number()),
    }),
  ),
});

const NTKQuickActionSchema = v.union([NTKQuickActionBehaviourMeta]);

export type NTKQuickAction = v.InferInput<typeof NTKQuickActionSchema>;

export function checkIsQuickActionValid(action: object) {
  return v.is(NTKQuickActionSchema, action);
}

export function getValidNTKQuickActions(actions: object[]) {
  return actions.filter((action) => checkIsQuickActionValid(action));
}

export function partialParseQuickAction(action: object) {
  return partialParse(NTKQuickActionSchema, action);
}

export function useNTKQuickActionUtils() {
  const { mutateAsync: createBehaviour } = useUpsertStudentBehaviour();
  const { mutateAsync: createHighlight } = useSaveTimelineHighlight();

  const getNTKQuickActionButtonProps = useCallback(
    (
      meta: NTKQuickAction,
      studentId: number,
      group: NeedToKnowGroup | undefined,
    ): ButtonProps => {
      switch (meta.__typename) {
        case 'NTKQuickActionBehaviourMeta':
          return {
            onClick: async () =>
              createBehaviour(
                [
                  {
                    note: meta.details,
                    referencedParties: studentId ? [studentId] : [],
                    tags: meta.tagIds,
                    associatedParties: group ? [group.partyId] : [],
                  },
                ],
                {
                  onSuccess: (newBehaviourResponse) => {
                    if (meta.highlight) {
                      const { title, description, assignedToPartyIds } =
                        meta.highlight;
                      createHighlight({
                        title,
                        highlight: description,
                        assignedToPartyIds,
                        studentPartyId: studentId,
                        events: newBehaviourResponse.notes_upsertNotes.map(
                          ({ id, tags }) => {
                            const behaviourType = tags![0]?.behaviourType;
                            const eventType =
                              eventTypeByBehaviourType[behaviourType!];

                            return {
                              eventId: id!.toString(),
                              eventType,
                            };
                          },
                        ),
                      });
                    }
                  },
                },
              ),
          };
        default:
          return {};
      }
    },
    [],
  );

  return { getNTKQuickActionButtonProps };
}
